import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { useQuote } from "../context/QuoteContext";
import { industries } from "../data/products";
import Seo from "../components/Seo";

const ease = [0.16, 1, 0.3, 1];

export default function Industries() {
  const { openQuote } = useQuote();
  return (
    <div data-testid="industries-page" className="pt-32">
      <Seo
        title="Industries We Serve | Clinixport Global"
        description="Clinixport Global supplies hospitals, clinics, laboratories, healthcare manufacturing, institutional healthcare and food processing, restaurants & catering — environments where cleanliness and contamination control are essential."
        path="/industries"
        keywords="hospital supplies, clinic supplies, laboratory hygiene, food processing hygiene, healthcare supply partner"
      />
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-20">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F6FEB] mb-5">Industries & Solutions</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter text-[#0A1128] leading-[1.02] max-w-4xl">
            Supply designed around cleanliness-critical environments.
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-7 text-lg text-[#475569] leading-relaxed max-w-2xl">
            We partner with organisations where hygiene and protection are non-negotiable —
            acting as a dependable extension of their standards, not just another vendor.
          </p>
        </Reveal>
      </section>

      <section className="relative">
        <div
          className="h-[300px] sm:h-[420px] bg-cover bg-center relative"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1778151270902-cb0ca572f2ee?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80')" }}
        >
          <div className="absolute inset-0 bg-[#0A1128]/55" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-[1400px] mx-auto px-5 sm:px-8 w-full pb-10">
              <p className="text-white/80 text-lg font-semibold max-w-xl">
                From hospital wards to laboratory cleanrooms — consistent supply, wherever standards apply.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-[#0A1128]/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 divide-y divide-[#0A1128]/10">
          {industries.map((ind, i) => (
            <motion.div
              key={ind.id}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease, delay: (i % 2) * 0.05 }}
              className="group grid grid-cols-1 md:grid-cols-12 gap-6 py-12 items-start"
              data-testid={`industry-${ind.id}`}
            >
              <span className="md:col-span-1 text-3xl font-extrabold tracking-tighter text-[#1F6FEB]">{ind.num}</span>
              <h3 className="md:col-span-4 text-2xl sm:text-3xl font-bold tracking-tight text-[#0A1128] leading-tight group-hover:text-[#1F6FEB] transition-colors">{ind.name}</h3>
              <p className="md:col-span-7 text-lg text-[#475569] leading-relaxed">{ind.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-[#0A1128] text-white py-28 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[1.05] max-w-3xl">
              Let's build a supply partnership that keeps your standards intact.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <button onClick={() => openQuote()} data-testid="industries-cta-quote"
              className="mt-10 inline-flex items-center gap-2 bg-[#1F6FEB] hover:bg-[#1556B8] transition-colors text-white px-9 py-4 font-bold text-sm tracking-widest uppercase">
              Request a Quote <ArrowRight size={18} />
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
